import { Breakpoints } from '@angular/cdk/layout';
import { Component, Inject, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom, from } from 'rxjs';
import { first } from 'rxjs/operators';

import { ClassName, RoleService } from '@sim/role';
import {
  BaseTableFilteredComponent,
  basicDeleteTemplateOptions,
  basicShowTemplateOptions,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { CardsApiService, filePath, SwalService, UIExportService } from '@atmi/core';
import { cardsOptions } from '@atmi/core/models';
import { required } from '@atmi/core/utils';
import { getRouteFromClassName } from '@sim/audit/utils';
import {
  CollectionExportDialogComponent
} from '@sim/collections/dialogs/collection-export-dialog/collection-export-dialog.component';
import { Collection, CollectionItem, CollectionItemFilter, CollectionItemFilterMeta } from '../../models/collection';
import { CollectionItemApiService } from '../../services/collection-item-api.service';
import { collectionClassNameLabels } from '../../utils';
import { CollectionItemsFiltersComponent } from '../collection-items-filters/collection-items-filters.component';

@Component({
  selector: 'app-collection-items',
  styleUrls: ['./collection-items.component.scss'],
  templateUrl: './collection-items.component.html',
})
export class CollectionItemsComponent
  extends BaseTableFilteredComponent<CollectionItem, CollectionItemFilter>
  implements UiTableComponent<CollectionItem> {

  readonly collectionClassNameLabels = collectionClassNameLabels;

  readonly MobileFiltersComponent = CollectionItemsFiltersComponent;

  private readonly collection$ = new BehaviorSubject<Collection | null>(null);

  defaultLimit = 100;

  @Input() set collection(collection: Collection | null) {
    if (collection) {
      this.collection$.next(collection);
    }
  }

  get collection() {
    return this.collection$.value;
  }

  readonly fields: TableField<CollectionItem>[] = [
    {
      context: (x) => x.previewUrl ? `${this.fileUrl}${x.previewUrl}` : undefined,
      header: this.translate.instant('Anteprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      context: (x) => x.titolo,
      header: this.translate.instant('Titolo'),
      key: 'titolo',
    },
    {
      context: (x) => collectionClassNameLabels[x.className],
      header: this.translate.instant('Tipo elemento'),
      key: 'className',
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
    },
    {
      context: (x) => x.descrizione,
      header: this.translate.instant('Descrizione'),
      key: 'descrizione',
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium],
    },
    {
      align: 'right',
      header: '',
      key: 'context',
      showBreakpoints: [Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium],
      template: 'context',
    },
    basicShowTemplateOptions,
  ];
//per far funzionare il cambio di paging e pagina bisogna rimuovere il pipe sulla this.collection$ che non cambia mai
//come toppa si e` messo defaultLimit = 100
  readonly pager$ = (page?: number, limit?: number, sort?: TableSortType, f?: TableFilterType) => {
    const collectionId = this.collection?.id;
    if (!collectionId) {
      return from(Promise.resolve({ count: 0, data: [] }));
    }
    return this.collectionItemApi.index(collectionId, { page, limit, sort, filter: f });
  };

  readonly contextMenus: Array<TableContextMenu<CollectionItem>> = [
    {
      action: (item) => this.router.navigate([getRouteFromClassName(item.className, item.objectId)]),
      label: this.translate.instant('Visualizza l\'oggetto'),
    },
  ];

  readonly cardOptions = cardsOptions;

  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly collectionItemApi: CollectionItemApiService,
    private readonly cardApiService: CardsApiService,
    private readonly roleService: RoleService,
    private readonly uiExportService: UIExportService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
    public dialog: MatDialog
  ) {
    super(translate, router, route, CollectionItemFilterMeta, swalService);
    
    // Debug logging
    console.log('CollectionItemsComponent - Current role:', this.roleService.role);
    console.log('CollectionItemsComponent - Role service:', this.roleService);
    
    const hasCapabilityToDelete = (['BeneOA', 'BeneF', 'BeneMI', 'BeneD', 'BeneS'] as ClassName[]).some(
      (className) => {
        const hasDelete = this.roleService.hasCapability(className, ['delete']);
        console.log(`CollectionItemsComponent - ${className} has delete:`, hasDelete);
        return hasDelete;
      }
    );
    
    console.log('CollectionItemsComponent - hasCapabilityToDelete:', hasCapabilityToDelete);

    if (hasCapabilityToDelete) {
      this.contextMenus.push({
        action: (item) => item.id
          ? this.deleteHandler(item.id, item, { text: 'Sei sicuro di voler procede?', title: 'Rimuovi oggetto' })
          : () => undefined,
        label: this.translate.instant('Rimuovi oggetto dalla collezione'),
      });
      this.fields.push(basicDeleteTemplateOptions);
      console.log('CollectionItemsComponent - Delete options added');
    } else {
      console.log('CollectionItemsComponent - Delete options NOT added');
    }
  }

  editShowHandler(item: CollectionItem): Promise<boolean> {
    return this.router.navigate([getRouteFromClassName(item.className, item.objectId)]);
  }

  deleteConcreteHandler(_: string, item: CollectionItem) {
    if (!this.collection?.id) {
      throw new Error('Cannot delete a collection item without the collection parent');
    }
    return this.collectionItemApi.remove(this.collection?.id, item.objectId);
  }

  // tslint:disable-next-line:variable-name
  deleteSelectedConcreteHandler(_ids: string[]) {
    return from(Promise.all([]));
  }


  /**
   * called by modal not here
   */
  async exportSelected() {

    const exportDialog = this.dialog.open(CollectionExportDialogComponent, {
      width: '60vw',
      panelClass: 'export-dialog-container',
      disableClose: true,
      data: {
        collectionId: this.collection?.id,
      }
    });
    exportDialog.afterClosed().subscribe((res: object) => {
      return res;
    });


  }

  async exportHandler() {
    this.uiExportService.manageExport(
      async (template?: string, format?: string) => {
        const collection = this.collection;
        required(collection?.id);
        const blob = await firstValueFrom(this.collectionItemApi.exportList(collection.id, template, format, {}).pipe(first()));
        this.selectedItems = [];
        return blob;
      },
      'OggettoCollezione',
      `elementi-collezione-${this.collection?.titolo}`,
    );
  }
}
